import React, { Component } from 'react';
import Inherit from '../../core/inherit';
import withNavigate from '../../core/withNavigate';
import {
    Layout,
    TableSkeleton,
    PageHeader,
    StatCard,
    SectionCard,
    FilterBar,
    FilterField,
    EmptyState,
    IconAction,
    StatusTag
} from '../common';
import { Select, DatePicker, Table, Button, Spin } from 'antd';
import { ChartColumn, FilterX, Store, Percent, ClipboardCheck, LayoutGrid, Image, ShieldAlert, TriangleAlert, RotateCw, Building2, Inbox } from 'lucide-react';
import dayjs from 'dayjs';
import ShareOfShelfController, { CHANNEL_OPTIONS, DEFAULT_FILTERS } from './shareOfShelf.controller';
import { sosMonthColumns, titleize } from './sosHeat';
import { monthLabel } from '../uso-de-shelf/usageHeat';
import { SosLegend } from './elements/HeatLegend';
import StoreCategoryDrawer from './StoreCategoryDrawer';
import { brand } from '../../theme/tokens';

const { RangePicker } = DatePicker;

/** "Meses: Ene 2026 · Feb 2026" footer under heat tables. */
const MonthsFooter = ({ months }) =>
    months.length ? (
        <div className="border-t border-surface-100 px-5 py-3 text-xs text-surface-400">Meses: {months.map(monthLabel).join(' · ')}</div>
    ) : null;

/** Colored tag dot for the Etiqueta select options. */
const tagOption = (opt) => (
    <span className="flex items-center gap-2">
        <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: opt.data.color || brand[500] }} />
        {opt.data.label}
    </span>
);

class ShareOfShelf extends Inherit(ShareOfShelfController, Component) {
    state = {
        loading: true,
        dataLoading: false,
        storesLoading: false,
        accessDenied: false,
        error: false,
        clientOptions: [],
        tagOptions: [],
        categoryOptions: [],
        storeTypeOptions: [], // Tipo de PDV options (StoreType.value)
        data: null, // { months, stats, categories }
        clientStores: null, // { months, stores }
        filters: { ...DEFAULT_FILTERS },
        dateRange: [dayjs().subtract(150, 'day'), dayjs()],
        selectedClientId: null,
        selectedClientName: null,
        table2Category: null,
        drawerOpen: false,
        drawerStore: null
    };

    // ---- Table 1: categories × months, expandable to clients ----
    get categoryColumns() {
        const months = this.state.data?.months || [];
        return [
            {
                title: 'Categoría',
                dataIndex: 'label',
                key: 'label',
                fixed: 'left',
                width: 240,
                render: (t, r) => (
                    <div className="flex items-center gap-2">
                        <span className="font-medium text-surface-900">{titleize(t)}</span>
                        <StatusTag className="!h-5 !px-1.5 tabular-nums">{r.clientCount} clientes</StatusTag>
                    </div>
                )
            },
            ...sosMonthColumns(months)
        ];
    }

    renderCategoryClients(category) {
        const months = this.state.data?.months || [];
        const columns = [
            {
                title: 'Cliente',
                dataIndex: 'clientName',
                key: 'clientName',
                fixed: 'left',
                width: 240,
                render: (t, r) => (
                    <button
                        type="button"
                        className="inline-flex items-center gap-1.5 text-left font-medium text-primary-600 hover:text-primary-700 hover:underline"
                        title="Ver PDV de este cliente"
                        onClick={() => this.selectClient(r.clientId, r.clientName)}>
                        <Building2 className="text-surface-400" />
                        {t}
                    </button>
                )
            },
            ...sosMonthColumns(months)
        ];
        return (
            <Table
                className="heat-table"
                columns={columns}
                dataSource={category.clients || []}
                rowKey={(r) => r.clientId || r.clientName}
                size="small"
                pagination={false}
                scroll={{ x: 'max-content' }}
            />
        );
    }

    // ---- Table 2: stores of the selected client (compiled or one category) ----
    get storeColumns() {
        const months = this.state.clientStores?.months || [];
        return [
            {
                title: 'PDV',
                dataIndex: 'storeName',
                key: 'storeName',
                fixed: 'left',
                width: 300,
                render: (t, r) => (
                    <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                            <button
                                type="button"
                                className="block text-left font-medium text-primary-600 hover:text-primary-700 hover:underline"
                                onClick={() => this.openStore(r)}>
                                {t}
                            </button>
                            <span className="text-xs text-surface-400">{r.storeType || 'Sin tipo'}</span>
                        </div>
                        <IconAction icon={<Image />} label="Ver imágenes de share" onClick={() => this.openStore(r)} />
                    </div>
                )
            },
            ...sosMonthColumns(months)
        ];
    }

    renderStats() {
        const stats = this.state.data?.stats || { totalChecks: 0, checksWithSos: 0, coverage: 0 };
        const cats = this.state.data?.categories?.length || 0;
        return (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4 mb-4">
                <StatCard label="Store-checks (rango)" value={stats.totalChecks.toLocaleString()} icon={<ClipboardCheck />} tone="brand" />
                <StatCard
                    label="Con Share of Shelf"
                    value={stats.checksWithSos.toLocaleString()}
                    icon={<ChartColumn />}
                    tone="violet"
                    hint="Store-checks con caras registradas"
                />
                <StatCard
                    label="Cobertura SOS"
                    value={`${(stats.coverage || 0).toFixed(1)}%`}
                    icon={<Percent />}
                    tone="success"
                    help="Store-checks con Share of Shelf sobre el total de store-checks del rango."
                />
                <StatCard label="Categorías" value={cats.toLocaleString()} icon={<LayoutGrid />} tone="info" />
            </div>
        );
    }

    renderTable2() {
        const { selectedClientId, selectedClientName, clientStores, storesLoading, table2Category, clientOptions, categoryOptions } = this.state;
        const months = clientStores?.months || [];

        return (
            <SectionCard
                flush
                className="mt-4"
                icon={<Store />}
                title="PDV del cliente"
                description="SOS por PDV y mes · haz clic en un PDV para ver su último levantamiento"
                extra={<SosLegend />}>
                <div className="flex flex-wrap items-end gap-3 border-b border-surface-100 px-5 py-3">
                    <FilterField label="Cliente" width={260}>
                        <Select
                            showSearch
                            allowClear
                            optionFilterProp="label"
                            placeholder="Selecciona un cliente"
                            value={selectedClientId}
                            options={clientOptions}
                            onChange={(v) => this.pickTable2Client(v)}
                        />
                    </FilterField>
                    <FilterField label="Categoría" width={220}>
                        <Select
                            allowClear
                            showSearch
                            optionFilterProp="label"
                            placeholder="Todas las categorías"
                            value={table2Category}
                            options={categoryOptions}
                            onChange={(v) => this.setTable2Category(v)}
                        />
                    </FilterField>
                </div>
                {!selectedClientId ? (
                    <EmptyState
                        icon={<Building2 />}
                        title="Elige un cliente"
                        description="Selecciona un cliente (arriba o desde la tabla de categorías) para ver sus PDV."
                    />
                ) : storesLoading ? (
                    <div className="flex justify-center py-12">
                        <Spin />
                    </div>
                ) : (
                    <>
                        <div className="flex flex-wrap items-center gap-2 px-5 py-3 text-xs text-surface-500">
                            <span className="font-medium text-surface-900">{selectedClientName}</span>
                            <span className="text-surface-300">·</span>
                            {table2Category ? `Categoría: ${this.table2CategoryLabel()}` : 'Todas las categorías (promedio por PDV)'}
                        </div>
                        <Table
                            className="heat-table"
                            columns={this.storeColumns}
                            dataSource={this.storeRows}
                            rowKey={(r) => r.storeId}
                            size="middle"
                            scroll={{ x: 'max-content' }}
                            pagination={{ pageSize: 25, showSizeChanger: true, pageSizeOptions: ['10', '25', '50', '100'], showTotal: (t) => `${t} PDV` }}
                            locale={{ emptyText: <EmptyState icon={<Inbox />} title="Sin datos" description="Sin PDV con share en el rango" /> }}
                        />
                        <MonthsFooter months={months} />
                    </>
                )}
            </SectionCard>
        );
    }

    renderFilters() {
        const { tagOptions, filters, dateRange, storeTypeOptions, clientOptions, selectedClientId } = this.state;

        return (
            <FilterBar
                actions={
                    <Button icon={<FilterX />} onClick={() => this.clearFilters()}>
                        Limpiar
                    </Button>
                }>
                <FilterField label="Canal" width={190}>
                    <Select
                        mode="multiple"
                        allowClear
                        maxTagCount="responsive"
                        placeholder="Todos"
                        value={filters.channel}
                        options={CHANNEL_OPTIONS}
                        onChange={(v) => this.setFilter('channel', v || [])}
                    />
                </FilterField>
                <FilterField label="Cliente" width={240}>
                    <Select
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        placeholder="Selecciona un cliente"
                        value={selectedClientId}
                        options={clientOptions}
                        onChange={(v) => this.pickClient(v)}
                    />
                </FilterField>
                <FilterField label="Tipo de PDV" width={200}>
                    <Select
                        mode="multiple"
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        maxTagCount="responsive"
                        placeholder="Todos"
                        value={filters.storeType}
                        options={storeTypeOptions}
                        onChange={(v) => this.setFilter('storeType', v || [])}
                    />
                </FilterField>
                <FilterField label="Etiqueta" width={210}>
                    <Select
                        mode="multiple"
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        maxTagCount="responsive"
                        placeholder="Todas"
                        value={filters.tagId}
                        options={tagOptions}
                        onChange={(v) => this.setFilter('tagId', v || [])}
                        optionRender={tagOption}
                    />
                </FilterField>
                <FilterField label="Rango de fechas" width={250}>
                    <RangePicker value={dateRange} allowClear={false} format="DD/MM/YYYY" onChange={(range) => this.setDateRange(range)} />
                </FilterField>
            </FilterBar>
        );
    }

    render() {
        const { loading, dataLoading, accessDenied, error, data, drawerOpen, drawerStore, selectedClientName } = this.state;
        const months = data?.months || [];

        return (
            <Layout current="share-of-shelf">
                {this.props.contextHolder}

                <PageHeader
                    icon={<ChartColumn />}
                    title="Share of Shelf"
                    description="Share of Shelf por categoría y mes (según el último levantamiento de cada PDV en cada mes). Expande una categoría para ver sus clientes; selecciona un cliente para ver sus PDV."
                />

                {accessDenied ? (
                    <div className="shelf-card">
                        <EmptyState
                            tone="danger"
                            icon={<ShieldAlert />}
                            title="Acceso restringido"
                            description="Esta vista está disponible para administradores y gerentes."
                        />
                    </div>
                ) : error ? (
                    <div className="shelf-card">
                        <EmptyState
                            tone="danger"
                            icon={<TriangleAlert />}
                            title="Error al cargar el Share of Shelf"
                            action={
                                <Button icon={<RotateCw />} onClick={() => this.retry()}>
                                    Reintentar
                                </Button>
                            }
                        />
                    </div>
                ) : loading ? (
                    <TableSkeleton rows={10} columns={8} />
                ) : (
                    <div className="animate-in">
                        {this.renderFilters()}

                        {this.renderStats()}

                        <SectionCard
                            flush
                            icon={<LayoutGrid />}
                            title="Share of Shelf por Categoría"
                            description="Más verde = mayor share · expande una categoría para ver sus clientes"
                            extra={<SosLegend />}>
                            <Table
                                className="heat-table"
                                columns={this.categoryColumns}
                                dataSource={data?.categories || []}
                                loading={dataLoading}
                                rowKey={(r) => r.key}
                                size="middle"
                                scroll={{ x: 'max-content' }}
                                expandable={{ expandedRowRender: (record) => this.renderCategoryClients(record), rowExpandable: (r) => (r.clients || []).length > 0 }}
                                pagination={false}
                                locale={{
                                    emptyText: <EmptyState icon={<Inbox />} title="Sin datos" description="Sin datos de share en el rango/filtros seleccionados" />
                                }}
                            />
                            <MonthsFooter months={months} />
                        </SectionCard>

                        {this.renderTable2()}
                    </div>
                )}

                <StoreCategoryDrawer
                    open={drawerOpen}
                    onClose={() => this.closeStore()}
                    data={this.data}
                    checkId={drawerStore?.latestCheckId}
                    storeName={drawerStore?.storeName}
                    clientName={selectedClientName}
                />
            </Layout>
        );
    }
}

export default withNavigate(ShareOfShelf);
